/**
 * useDeviceManagement
 *
 * Owns the FCM device-management UI state for UserSettingsModal:
 * enable/disable toggles (with debounce) and the remove-device
 * confirmation flow.
 */

import { useState, useCallback } from 'react';
import type { ToastType } from '../../../store/toastStore';

interface UseDeviceManagementParams {
  updateDeviceEnabled: (deviceId: string, enabled: boolean) => Promise<boolean>;
  removeDevice: (deviceId: string) => Promise<boolean>;
  showToast: (message: string, type?: ToastType) => void;
}

export function useDeviceManagement({
  updateDeviceEnabled,
  removeDevice,
  showToast,
}: UseDeviceManagementParams) {
  const [deviceToRemove, setDeviceToRemove] = useState<string | null>(null);
  const [isRemovingDevice, setIsRemovingDevice] = useState(false);
  const [pendingToggleDeviceId, setPendingToggleDeviceId] = useState<string | null>(null);

  /**
   * Handle toggling a device's enabled status with debounce
   */
  const handleDeviceToggle = useCallback(async (deviceId: string, enabled: boolean) => {
    // Prevent rapid toggles
    if (pendingToggleDeviceId) return;

    setPendingToggleDeviceId(deviceId);
    try {
      const success = await updateDeviceEnabled(deviceId, enabled);
      if (success) {
        showToast(
          enabled ? 'Device notifications enabled' : 'Device notifications disabled',
          'success'
        );
      } else {
        showToast('Failed to update device settings', 'error');
      }
    } catch {
      showToast('Failed to update device settings', 'error');
    } finally {
      setPendingToggleDeviceId(null);
    }
  }, [pendingToggleDeviceId, updateDeviceEnabled, showToast]);

  /**
   * Handle device removal with confirmation
   */
  const handleDeviceRemove = useCallback(async () => {
    if (!deviceToRemove) return;

    setIsRemovingDevice(true);
    try {
      const success = await removeDevice(deviceToRemove);
      if (success) {
        showToast('Device removed', 'success');
      } else {
        showToast('Failed to remove device', 'error');
      }
    } catch {
      showToast('Failed to remove device', 'error');
    } finally {
      setIsRemovingDevice(false);
      setDeviceToRemove(null);
    }
  }, [deviceToRemove, removeDevice, showToast]);

  return {
    deviceToRemove,
    setDeviceToRemove,
    isRemovingDevice,
    pendingToggleDeviceId,
    handleDeviceToggle,
    handleDeviceRemove,
  };
}
